import { TERMINATE_CODE } from "../components/Terminated/terminateCodes"
import { acceptableProgressStanding } from "./acceptableProgressStanding"

/**
 * Advancement gates are not "in-level work" for Terminated Level Complete.
 * When only purchase, enrollment, or reapply remains open, the terminated level is done.
 */
const ADVANCEMENT_GATE_KEYS = new Set(["purchase", "enrollment", "reapply"])

const COMPLETE_STATUSES = new Set(["complete", "passed", "approved"])

/**
 * Reapply status on the effective terminated level
 * (`currentLevelKey || mostRecentCompletedLevelKey`). A stale row on another
 * level is ignored.
 */
export function getCurrentLevelReapplyStatus(pgmProgress) {
  const levels = pgmProgress?.levels
  if (!Array.isArray(levels)) {
    return null
  }

  const effectiveLevelKey =
    pgmProgress?.currentLevelKey || pgmProgress?.mostRecentCompletedLevelKey
  if (!effectiveLevelKey) {
    return null
  }

  const level = levels.find(entry => entry.key === effectiveLevelKey)
  if (!level) {
    return null
  }

  const requirement = (level.requirements || []).find(
    entry => entry.key === "reapply",
  )
  return requirement?.status ?? null
}

/**
 * Check whether a reapply has been approved, signaling the ability to purchase next PGM Level
 */
export function isReapplyApprovedFromPgmProgress(pgmProgress) {
  return getCurrentLevelReapplyStatus(pgmProgress) === "approved"
}

/**
 * Live standing is Acceptable Progress Termination (terminate code GK).
 */
export function isAcceptableProgressTerminatedStanding(me) {
  return (
    me?.termination?.status === "Terminated" &&
    me?.termination?.terminateCd === TERMINATE_CODE.ACCEPTABLE_PROGRESS
  )
}

/**
 * Same "current level requirements satisfied" signal PGM Progress Surfaces use:
 * in-level work complete; purchase / enrollment may still be open.
 * When currentLevelKey is null and mostRecentCompletedLevelKey is set, Journey
 * treats that as level complete awaiting next purchase — Terminated Level Complete.
 */
export function isTerminatedLevelCompleteFromPgmProgress(pgmProgress) {
  const levels = pgmProgress?.levels
  if (!Array.isArray(levels)) {
    return false
  }

  const currentLevelKey = pgmProgress?.currentLevelKey
  const mostRecentCompletedLevelKey = pgmProgress?.mostRecentCompletedLevelKey

  // Journey: currentLevelKey is null when the current level is complete and the
  // next level has not been purchased — fall back to mostRecentCompletedLevelKey.
  if (!currentLevelKey) {
    return Boolean(mostRecentCompletedLevelKey)
  }

  const currentLevel = levels.find(level => level.key === currentLevelKey)
  if (!currentLevel) {
    return false
  }

  const requirements = currentLevel.requirements
  if (!requirements || requirements.length === 0) {
    return currentLevel.status === "complete"
  }

  return requirements.every(requirement => {
    if (ADVANCEMENT_GATE_KEYS.has(requirement.key)) {
      return true
    }

    if (requirement.key === "playing_ability_test") {
      return (
        COMPLETE_STATUSES.has(requirement.status) ||
        requirement.status === "qualified"
      )
    }

    return COMPLETE_STATUSES.has(requirement.status)
  })
}

/**
 * Resolve Acceptable Progress standing for PGM Progress Surfaces from person data.
 */
export function resolveAcceptableProgressStanding({
  me,
  pgmProgress,
  isApplicationApproved = false,
  isEligibilityRestored = false,
}) {
  return acceptableProgressStanding({
    isAcceptableProgressTerminated: isAcceptableProgressTerminatedStanding(me),
    isTerminatedLevelComplete:
      isTerminatedLevelCompleteFromPgmProgress(pgmProgress),
    isApplicationApproved:
      isApplicationApproved || isReapplyApprovedFromPgmProgress(pgmProgress),
    isEligibilityRestored,
  })
}
